import { type CurrencyCode } from '@/modules/location/constants';
import { getProductPreview } from '@/modules/pictures/helpers/getProductPreview';
import { picturePreviewAdapter } from '@/modules/pictures/helpers.ts';
import {
  type ProductBySlugResponse,
  type ProductBySlugExtendedResponse,
} from '@/modules/product/aliases';
import { isSold } from '@/modules/product/helpers/isSold';
import { toProductPicture } from '@/modules/product/helpers/toProductMedia';
import { type RecentlyViewedObject } from '@/modules/recentlyViewed/aliases';
import {
  MAX_PRODUCT_CARDS,
  MAX_STORED_RECENTLY_VIEWED,
  RECENTLY_VIEWED_PRODUCTS_LOCAL_STORAGE_KEY,
} from '@/modules/recentlyViewed/constants';
import {
  type LegacyLocallyStoredRecentlyViewedProduct,
  type LocallyStoredRecentlyViewedProduct,
  type RecentlyViewedProduct,
} from '@/modules/recentlyViewed/types';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';

function createRecentlyViewedProduct(
  product: ProductBySlugResponse | RecentlyViewedObject,
  { pricing, sizes }: Pick<ProductBySlugExtendedResponse, 'pricing' | 'sizes'>
): RecentlyViewedProduct {
  return {
    id: product.id,
    slug: product.slug,
    preview: getProductPreview(product),
    pictures:
      product.pictures
        ?.slice(0, 2)
        .map((picture) => picturePreviewAdapter(toProductPicture(picture))) ??
      [],
    has_video: product?.videos ? product.videos.length > 0 : false,
    pricing: {
      ...pricing,
      currency_name: pricing.currency as CurrencyCode,
    },
    sizes: (sizes?.filter((size) => size.name).map(({ name }) => name) ??
      []) as string[],
    status: product.status,
    sold: isSold(product.status),
    brand_id: product.brand_id,
    brand_name: product.brand_name,
  };
}

export function createLocallyStoredRecentlyViewedProduct(
  product: ProductBySlugResponse,
  productExtended: ProductBySlugExtendedResponse,
  overrides?: Partial<LocallyStoredRecentlyViewedProduct>
): LocallyStoredRecentlyViewedProduct {
  return {
    ...createRecentlyViewedProduct(product, productExtended),
    timestamp: Date.now(),
    ...overrides,
  };
}

export function mapRecentlyViewedObjectToProduct(
  object: RecentlyViewedObject
): RecentlyViewedProduct {
  return {
    ...createRecentlyViewedProduct(object, object),
    like_count: object.like_count,
    is_liked: object.is_liked,
  };
}

export function getLocallyStoredRecentlyViewedProducts(): LocallyStoredRecentlyViewedProduct[] {
  const localProducts = getLocalStorageItem(
    RECENTLY_VIEWED_PRODUCTS_LOCAL_STORAGE_KEY
  );

  const parsedProducts: (
    | LocallyStoredRecentlyViewedProduct
    | LegacyLocallyStoredRecentlyViewedProduct
  )[] = safeJsonParse(localProducts, []);

  const filteredProducts = parsedProducts.filter(
    (product): product is LocallyStoredRecentlyViewedProduct =>
      'pricing' in product && 'display_price' in product.pricing
  );

  /*
    Entries written before a pricing format change (the legacy `price` shape, or
    `pricing` without `display_price`) can't be rendered any more. If we detect
    any, we just delete the localStorage items.
  */
  if (filteredProducts.length < parsedProducts.length) {
    removeLocalStorageItem(RECENTLY_VIEWED_PRODUCTS_LOCAL_STORAGE_KEY);
    return [];
  }

  // Cap on read so users don't send an oversized sync payload on their next login.
  return filteredProducts.slice(0, MAX_STORED_RECENTLY_VIEWED);
}

export function addProductToLocalStorage(
  newProduct: LocallyStoredRecentlyViewedProduct
) {
  const localProducts = getLocallyStoredRecentlyViewedProducts();
  const filteredProducts = localProducts.filter(
    (existingProduct) => existingProduct.id !== newProduct.id
  );

  setLocalStorageItem(
    RECENTLY_VIEWED_PRODUCTS_LOCAL_STORAGE_KEY,
    JSON.stringify(
      [newProduct, ...filteredProducts].slice(0, MAX_STORED_RECENTLY_VIEWED)
    )
  );
}

export function getProductCardColumns(productsCount: number) {
  const MIN_COLUMNS = 4;

  if (productsCount < MIN_COLUMNS) {
    return MIN_COLUMNS;
  }
  if (productsCount > MAX_PRODUCT_CARDS) {
    return MAX_PRODUCT_CARDS;
  }
  return productsCount;
}
